import React, { useState, useEffect } from "react";
import { getToken } from "../lib/auth";
import { useAuth } from "../context/AuthContext";
import { AppShell } from "../components/Nav";

const TABS = [
  { id: "account", label: "Account" },
  { id: "rounds", label: "Rounds & game" },
  { id: "video", label: "Video privacy" },
  { id: "filters", label: "Filters" },
  { id: "notifications", label: "Notifications" },
  { id: "safety", label: "Safety & privacy" },
];

export default function Settings() {
  const { user } = useAuth();
  const [tab, setTab] = useState("account");
  const [minAge, setMinAge] = useState(18);
  const [maxAge, setMaxAge] = useState(50);
  const [relocation, setRelocation] = useState(false);
  const [filterSaving, setFilterSaving] = useState(false);
  const [filterMsg, setFilterMsg] = useState("");

  useEffect(() => {
    const token = getToken();
    if (!token) return;
    fetch("/api/me", { headers: { Authorization: "Bearer " + token } })
      .then((r) => (r.ok ? r.json() : null))
      .then((u) => {
        if (!u) return;
        if (u.min_age != null) setMinAge(u.min_age);
        if (u.max_age != null) setMaxAge(u.max_age);
        if (u.open_to_relocation != null) setRelocation(u.open_to_relocation);
      })
      .catch(() => {});
  }, []);

  const saveFilters = async () => {
    setFilterSaving(true); setFilterMsg("");
    const token = getToken();
    try {
      const res = await fetch("/api/me/preferences", {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
        body: JSON.stringify({ min_age: minAge, max_age: maxAge, open_to_relocation: relocation }),
      });
      setFilterMsg(res.ok ? "Saved." : "Error saving.");
    } catch { setFilterMsg("Error saving."); }
    finally { setFilterSaving(false); }
  };

  return (
    <AppShell>
      <div className="px-6 md:px-12 py-10 md:py-14 max-w-5xl">
        <div className="text-[11px] tracking-[0.28em] uppercase text-[#D4AF37] mb-3">
          Settings
        </div>
        <h1 className="font-display text-4xl md:text-5xl leading-tight mb-10">
          A quiet control room.
        </h1>

        <div className="grid md:grid-cols-[220px_1fr] gap-8">
          <aside className="space-y-1">
            {TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`tab-${t.id}`}
                className={`w-full text-left text-sm rounded-xl px-3 py-2.5 ${
                  tab === t.id ? "bg-white/[0.05] text-white ring-1 ring-[rgba(212,175,55,0.25)]" : "text-white/60 hover:text-white hover:bg-white/[0.03]"
                }`}
              >
                {t.label}
              </button>
            ))}
          </aside>

          <div className="space-y-5">
            {tab === "account" && (
              <>
                <Section title="Profile">
                  {[
                    ["Display name", user?.name || "—"],
                    ["Email", user?.email || "—"],
                    ["City", [user?.city, user?.state].filter(Boolean).join(", ") || "—"],
                    ["Pronouns", user?.pronouns || "—"],
                  ].map(([k, v]) => (
                    <Row key={k} label={k} value={v} />
                  ))}
                </Section>
                <Section title="Membership">
                  <Row label="Plan" value={user?.role === "admin" ? "Admin" : (user?.plan || "Free")} pill={user?.role === "admin" ? "Admin" : undefined} />
                  <Row label="Next billing" value={user?.next_billing || "—"} />
                </Section>
              </>
            )}

            {tab === "rounds" && (
              <>
                <Section title="Round behavior">
                  {[
                    ["Blind chemistry round", false],
                    ["Reveal finalists before confirmation", true],
                    ["AI finalist recommendation", true],
                    ["Allow one revisit per month", true],
                  ].map(([k, v]) => (
                    <Toggle key={k} label={k} defaultOn={v} />
                  ))}
                </Section>
              </>
            )}

            {tab === "video" && (
              <Section title="Video intro privacy">
                {[
                  ["Show intro video only after Round 2", true],
                  ["Allow video prompts in any round", false],
                  ["Let finalists see your intro", true],
                ].map(([k, v]) => (
                  <Toggle key={k} label={k} defaultOn={v} />
                ))}
              </Section>
            )}

            {tab === "filters" && (
              <Section title="Filters & dealbreakers">
                <div className="py-3 space-y-4">
                  <div>
                    <div className="text-sm text-white/70 mb-2">Age range</div>
                    <div className="flex items-center gap-3">
                      <input type="number" min="18" max="80" value={minAge}
                        onChange={(e) => setMinAge(+e.target.value)}
                        className="w-20 rounded-lg bg-white/[0.05] border border-white/10 px-3 py-1.5 text-sm text-white text-center outline-none focus:border-[rgba(212,175,55,0.4)]" />
                      <span className="text-white/40 text-sm">to</span>
                      <input type="number" min="18" max="80" value={maxAge}
                        onChange={(e) => setMaxAge(+e.target.value)}
                        className="w-20 rounded-lg bg-white/[0.05] border border-white/10 px-3 py-1.5 text-sm text-white text-center outline-none focus:border-[rgba(212,175,55,0.4)]" />
                      <span className="text-white/40 text-sm">years old</span>
                    </div>
                  </div>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" checked={relocation} onChange={(e) => setRelocation(e.target.checked)} className="accent-[#D4AF37]" />
                    <span className="text-sm text-white/70">Open to relocation</span>
                  </label>
                  {filterMsg && <p className={"text-xs " + (filterMsg === "Saved." ? "text-emerald-400" : "text-red-400")}>{filterMsg}</p>}
                  <button onClick={saveFilters} disabled={filterSaving}
                    className="rounded-full bg-gradient-gold text-[#080B14] font-semibold px-5 py-2 text-sm glow-gold-soft disabled:opacity-40">
                    {filterSaving ? "Saving…" : "Save filters"}
                  </button>
                </div>
              </Section>
            )}

            {tab === "notifications" && (
              <Section title="Notifications">
                {[
                  ["New round ready", true],
                  ["Top match messages", true],
                  ["Weekly reflection email", false],
                  ["Product updates", false],
                ].map(([k, v]) => (
                  <Toggle key={k} label={k} defaultOn={v} />
                ))}
              </Section>
            )}

            {tab === "safety" && (
              <Section title="Safety & privacy">
                <Row label="Two-factor" value="Enabled" pill="Secure" />
                <Row label="Hidden profiles" value="2" />
                <Row label="Saved profiles" value="14" />
              </Section>
            )}
          </div>
        </div>
      </div>
    </AppShell>
  );
}

function Section({ title, children }) {
  return (
    <div className="glass rounded-3xl p-6">
      <div className="text-[11px] tracking-[0.24em] uppercase text-[#D4AF37] mb-4">{title}</div>
      <div className="divide-y divide-white/5">{children}</div>
    </div>
  );
}

function Row({ label, value, pill }) {
  return (
    <div className="flex items-center justify-between py-3">
      <div className="text-sm text-white/70">{label}</div>
      <div className="flex items-center gap-2">
        <div className="font-display">{value}</div>
        {pill && (
          <span className="text-[10px] uppercase tracking-[0.2em] text-[#8FD6A6] bg-[rgba(74,163,106,0.1)] rounded-full px-2 py-0.5">
            {pill}
          </span>
        )}
      </div>
    </div>
  );
}

function Toggle({ label, defaultOn }) {
  const [on, setOn] = useState(!!defaultOn);
  return (
    <div className="flex items-center justify-between py-3">
      <div className="text-sm text-white/80">{label}</div>
      <button
        onClick={() => setOn((x) => !x)}
        data-testid={`toggle-${label.replace(/\s/g, "-").toLowerCase()}`}
        className={`relative h-6 w-11 rounded-full transition-colors ${on ? "bg-gradient-gold" : "bg-white/10"}`}
      >
        <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${on ? "translate-x-5" : "translate-x-0.5"}`} />
      </button>
    </div>
  );
}
